import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import { debounce } from 'lodash';
import Icon from "../../../../base/components/Icon";
import ToastService from "../../../../services/ToastService";
import SampleService from "../../../../services/SampleService";
import TableSearch from "../../../../base/components/Table/tableSearch";
import Table from '../../../../base/components/Table';
import {
  usePaginationProvider,
  useSearchProvider,
  useLocationSource
} from '../../../../base/components/Table/hooks';
import { LIMIT_OPTIONS } from "../../../../base/constants/pagination";
import { BUTTON_COLORS } from "../../../../base/components/Button/appearance";
import { isServiceEditable } from "../../../../base/helpers/adminRolePermissions";

const NotActivatedSamplesTab = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState(null);
  const [ihProductId, setIHProductId] = useState("");
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const toastService = new ToastService();
  const sampleService = new SampleService();
  const locationSource = useLocationSource();

  const paginationProvider = usePaginationProvider({
    source: locationSource,
    alias: "offset",
    scope: "",
    fallback: 0
  });

  const limitProvider = usePaginationProvider({
    source: locationSource,
    alias: "limit",
    scope: "",
    fallback: 10,
    onApplyClearScope: ["offset"]
  });

  const searchProvider = useSearchProvider({
    source: locationSource,
    scope: "",
    alias: 'search',
    onApplyClearScope: ["offset"]
  });

  const offset = paginationProvider.getValue() || 0;
  const limit = limitProvider.getValue() || 20;
  const search = searchProvider.getValue() || "";

  useEffect(() => {
    fetchSamples(search, Math.floor(offset / limit) + 1, limit);
  }, [offset, limit, search]);

  const fetchSamples = async (searchQuery = "", pageArg = 1, pageSizeArg = 20) => {
    setIsLoading(true);
    try {
      const response = await sampleService.getReceivedButNotActivatedSamples({
        limit: pageSizeArg,
        offset: (pageArg - 1) * pageSizeArg,
        orderBy: "receivedByLabWithoutActivationAt",
        orderType: "desc",
        query: searchQuery,
      });
      setSamples(response.data || []);
      setTotalCount(response.pagination?.totalCount || 0);
    } catch (err) {
      toastService.error("Error fetching samples");
      setSamples([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      searchProvider.setValue(searchTerm);
    }, 500),
    [searchProvider]
  );

  const handleSearchChange = (value) => {
    searchProvider.setValue(value);
    debouncedSearch(value);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setIHProductId("");
  };

  const handleAddNew = () => {
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!ihProductId || !ihProductId.trim()) {
      toastService.error("Please enter IHProductID");
      return;
    }

    try {
      const response = await sampleService.addReceivedButNotActivatedSample(ihProductId);
      if (response.status === "INVALID") {
        toastService.error(response.reason);
        return;
      }
      toastService.success("New Sample Added");
      setModalOpen(false);
      setIHProductId("");
      fetchSamples(search, Math.floor(offset / limit) + 1, limit);
    } catch (err) {
      const errorMessage = err.response?.data?.reason || err.response?.data?.message || "Internal server error";
      toastService.error(errorMessage);
    }
  };

  const handleDeleteClick = (sample) => {
    setSampleToDelete(sample);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sampleToDelete) return;
    
    try {
      await sampleService.deleteReceivedButNotActivatedSample(sampleToDelete.IHProductId);
      toastService.success("Sample deleted successfully");
      setDeleteModalOpen(false);
      setSampleToDelete(null);
      fetchSamples(search, Math.floor(offset / limit) + 1, limit);
    } catch (err) {
      const errorMessage = err.response?.data?.reason || err.response?.data?.message || "Error deleting sample";
      toastService.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSampleToDelete(null);
  };

  const isOlderThan14Days = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
    return diffInDays > 14;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const columns = [
    {
      Header: "Sample ID",
      accessor: "sampleId"
    },
    {
      Header: "IH Product ID",
      accessor: "IHProductId"
    },
    {
      Header: "Product Name",
      accessor: "productName"
    },
    {
      Header: "Lab Profile ID",
      accessor: "labProfileId"
    },
    {
      Header: "Added At",
      accessor: "addedAt",
      Cell: ({ value }) => (
        <div className="d-flex align-items-center">
          {formatDate(value)}
          {isOlderThan14Days(value) && (
            <Icon
              icon="infoCircle"
              className="ms-2 text-warning"
              style={{ fontSize: '1.2em' }}
            />
          )}
        </div>
      )
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <Button
          color="danger"
          size="sm"
          onClick={() => handleDeleteClick(row.original)}
        >
          Delete
        </Button>
      )
    }
  ];

  const getFilteredColumns = () => {
    const isSamplesPageEditable = isServiceEditable("Samples page");

    if (isSamplesPageEditable) {
      return columns;
    }
    return columns.filter(col => col.accessor !== "actions");
  };

  const commonPlaceholder = (
      <div className="text-center">
        No samples found.
      </div>
  );

  const placeholderForSearch = (
      <div className="text-center">
        No matching samples.
      </div>
  );  

  const isSamplesPageEditable = isServiceEditable("Samples page");

  return (
    <>
      <section className="mb-3 d-flex align-items-center justify-content-between">
        <section className="d-flex align-items-center">
          <TableSearch
            search={search}
            onSearch={val => searchProvider.setValue(val)}
            placeholder="Search by Sample ID and IH Product ID"
            className="notification-search"
            style={{ maxWidth: 400 }}
          />
        </section>
        {isSamplesPageEditable &&
          <Button
            type="button"
            color={BUTTON_COLORS.primary}
            onClick={handleAddNew}
          >
            <i className="mdi mdi-plus me-1"/>
            Add new
          </Button>
        }
      </section>

      <Table
        columns={getFilteredColumns()}
        data={samples}
        loading={isLoading}
        totalCount={totalCount}
        limit={limit}
        offset={offset}
        paginationProvider={paginationProvider}
        searchProvider={searchProvider}
        isLimitEditable
        limitProvider={limitProvider}
        limitOptions={LIMIT_OPTIONS}
        className="table-centered table-nowrap"
        commonPlaceholder={commonPlaceholder}
        placeholderForSearch={placeholderForSearch}
      />

      {/* Add New Sample Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Add new not activated sample
        </ModalHeader>
        <ModalBody>
          <Input
            type="text"
            placeholder="IHProductID"
            value={ihProductId}
            onChange={(e) => setIHProductId(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit}>
            Submit
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleDeleteCancel}>
        <ModalHeader toggle={handleDeleteCancel}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          {sampleToDelete && (
            <p>
              Are you sure you want to delete sample{" "}
              <strong>{sampleToDelete.sampleId}</strong> (
              <strong>{sampleToDelete.IHProductId}</strong>) from the list?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
          <Button color="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default NotActivatedSamplesTab;
